import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsentBanner from "@/components/ConsentBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import SEO from "@/components/SEO";
import StickyWhatsAppCTA from "@/components/StickyWhatsAppCTA";
import SkipLink from "@/components/SkipLink";

// Pages
import Home from "@/pages/Home";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import NotFound from "@/pages/NotFound";

// Lib
import { initGA, logPageView } from "@/lib/analytics";

// Analytics Tracker Component
const AnalyticsTracker = ({ allowed }) => {
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (allowed && !initialized) {
      initGA();
      setInitialized(true);
    }
  }, [allowed, initialized]);

  useEffect(() => {
    if (allowed && initialized) {
      logPageView();
    }
  }, [location, allowed, initialized]);

  return null;
};

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Alice Bot",
  "url": "https://s00k-alice.vercel.app/",
  "applicationCategory": "SocialNetworkingApplication",
  "operatingSystem": "WhatsApp",
  "author": {
    "@type": "Person",
    "name": "Stylish00Killer"
  },
  "offers": {
    "@type": "AggregateOffer",
    "lowPrice": "1499",
    "highPrice": "4999",
    "priceCurrency": "INR",
    "offerCount": "3"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "1250",
    "bestRating": "5",
    "worstRating": "1"
  },
  "description": "Alice Bot is the ultimate WhatsApp group automation tool featuring moderation, games, stickers, and 24/7 support.",
  "image": "https://s00k-alice.vercel.app/icon.webp"
};

// Main App Component
function App() {
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AnalyticsTracker allowed={analyticsAllowed} />
          <div className="min-h-screen bg-[#050505] overflow-x-hidden">
            <SkipLink />
            <Navbar />

            <main id="main-content">
              <Routes>
                <Route path="/" element={<><SEO schema={homeSchema} /><Home /></>} />
                <Route path="/privacy" element={<><SEO title="Privacy Policy - Alice Bot" description="Read our privacy policy to understand how we handle your data." /><PrivacyPolicy /></>} />
                <Route path="/terms" element={<><SEO title="Terms of Service - Alice Bot" description="Read our terms of service for Alice Bot WhatsApp automation." /><TermsOfService /></>} />
                <Route path="*" element={<><SEO title="404 - Not Found" description="The page you are looking for does not exist." /><NotFound /></>} />
              </Routes>
            </main>

            <Footer />
            <StickyWhatsAppCTA />

            <ConsentBanner onConsentChange={setAnalyticsAllowed} />

            {analyticsAllowed && (
              <>
                <Analytics />
                <SpeedInsights />
              </>
            )}
          </div>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
